import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  UserPlus,
  UserCheck,
  ArrowLeft,
  Users,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { CommunityCard } from "../components/community/CommunityCard";
import { PostDetailModal } from "../components/community/PostDetailModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityProfilePage = () => {
  const { userId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPost, setOpenPost] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/community/users/${userId}`, {
          headers: authHeader,
        });
        if (alive) setProfile(r.data);
      } catch (e) {
        toast.error("Profile not found.");
        navigate("/dashboard/community");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId]);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  const handleFollow = async () => {
    setFollowBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/users/${userId}/follow`,
        {},
        { headers: authHeader }
      );
      setProfile((prev) => ({
        ...prev,
        is_following: r.data.following,
        stats: { ...prev.stats, followers_count: r.data.followers_count },
      }));
    } catch (e) {
      toast.error("Follow failed.");
    } finally {
      setFollowBusy(false);
    }
  };

  const handleLike = async (post) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/community/posts/${post.id}/like`,
        {},
        { headers: authHeader }
      );
      setProfile((prev) => ({
        ...prev,
        posts: prev.posts.map((p) =>
          p.id === post.id
            ? { ...p, liked_by_me: r.data.liked, likes_count: r.data.likes_count }
            : p
        ),
      }));
    } catch (e) {
      toast.error("Could not update like.");
    }
  };

  const handlePostUpdate = (updated) => {
    setProfile((prev) => ({
      ...prev,
      posts:
        updated.status === "removed"
          ? prev.posts.filter((p) => p.id !== updated.id)
          : prev.posts.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
      </div>
    );
  }

  if (!profile) return null;

  const { user, stats, is_following, is_me, posts } = profile;

  return (
    <div className="space-y-8 px-4 lg:px-0" data-testid="community-profile-page">
      <Link
        to="/dashboard/community"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-ink-500 hover:text-ink-950"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to community
      </Link>

      {/* Profile header */}
      <div className="bg-gradient-to-br from-ink-950 to-ink-800 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-coral-500/20 rounded-full blur-3xl"></div>
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-coral-500 text-white flex items-center justify-center text-4xl font-black shrink-0 border-4 border-white/20">
            {user.initial}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-black tracking-tight" data-testid="community-profile-name">
              {user.name}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-coral-300" />
                <strong>{stats.posts_count}</strong> posts
              </span>
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3 text-coral-300" />
                <strong>{stats.followers_count}</strong> followers
              </span>
              <span className="text-ink-300">
                <strong>{stats.following_count}</strong> following
              </span>
            </div>
            {user.subscription_tier && user.subscription_tier !== "free" && (
              <span className="inline-block mt-3 px-2 py-1 rounded-full bg-coral-500/20 text-coral-300 text-[10px] uppercase tracking-[0.18em] font-bold">
                {user.subscription_tier}
              </span>
            )}
          </div>
          {!is_me && (
            <Button
              onClick={handleFollow}
              disabled={followBusy}
              data-testid="community-profile-follow-btn"
              className={is_following ? "bg-white text-ink-950" : "bg-coral-500 text-white"}
            >
              {is_following ? (
                <>
                  <UserCheck className="w-4 h-4" />
                  Following
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Follow
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Posts */}
      {posts.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-ink-200 p-12 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-ink-300 mb-3" />
          <p className="text-sm text-ink-500">
            {is_me ? "You haven't shared anything yet." : "No posts to show yet."}
          </p>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
          {posts.map((p) => (
            <CommunityCard
              key={p.id}
              post={p}
              onOpen={(post) => setOpenPost(post)}
              onLike={handleLike}
            />
          ))}
        </div>
      )}

      {openPost && (
        <PostDetailModal
          post={openPost}
          onClose={() => setOpenPost(null)}
          onUpdate={handlePostUpdate}
        />
      )}
    </div>
  );
};

export default CommunityProfilePage;
